import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Swords, Trophy, X as XIcon, Clock, Check } from "lucide-react";
import { GufoIcon } from "@/components/GufoIcon";

const STATUS_META = {
  pending:  { label: "In attesa",   color: "#f59e0b", icon: Clock },
  accepted: { label: "Accettata",   color: "#0ea5e9", icon: Swords },
  resolved: { label: "Conclusa",    color: "#10b981", icon: Trophy },
  declined: { label: "Rifiutata",   color: "#ef4444", icon: XIcon },
  expired:  { label: "Scaduta",     color: "#94a3b8", icon: Clock },
  draw:     { label: "Pareggio",    color: "#94a3b8", icon: Trophy },
  voided:   { label: "Annullata",   color: "#94a3b8", icon: XIcon },
};

export default function TuttoONienteCard({ user, onAction }) {
  const [status, setStatus] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [targets, setTargets] = useState([]);
  const [target, setTarget] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/me/game-status");
      setStatus(data);
    } catch {}
    try {
      const { data } = await api.get("/me/tutto-o-niente");
      setChallenges(data);
    } catch {}
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (openCreate) {
      api.get("/public/teams").then(({ data }) => setTargets(data)).catch(() => {});
    }
  }, [openCreate]);

  const availableTargets = useMemo(
    () => targets.filter((t) => t.user_id !== user?.id),
    [targets, user?.id]
  );

  const createChallenge = async () => {
    if (!target) return toast.error("Scegli l'avversario");
    setBusy(true);
    try {
      await api.post("/me/tutto-o-niente", { target_user_id: target, message });
      toast.success("Sfida lanciata! L'avversario deve accettare entro la fine della giornata.");
      setOpenCreate(false);
      setTarget(""); setMessage("");
      await reload();
      onAction && onAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio sfida");
    } finally { setBusy(false); }
  };

  const respond = async (id, accept) => {
    setBusy(true);
    try {
      await api.post(`/me/tutto-o-niente/${id}/${accept ? "accept" : "decline"}`);
      toast.success(accept ? "Sfida accettata!" : "Sfida rifiutata");
      await reload();
      onAction && onAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const incomingPending = challenges.filter(
    (c) => c.status === "pending" && c.target_user_id === user?.id
  );
  const recent = challenges.slice(0, 6);
  const active = status?.active_challenge;

  return (
    <div className="card-surface rounded-2xl p-5" data-testid="tutto-o-niente-card"
         style={{ borderColor: "rgba(239,68,68,0.25)" }}>
      <div className="flex items-start gap-3 flex-wrap justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
            <Swords className="w-3.5 h-3.5" style={{ color: "#ef4444" }} />
            Tutto o Niente
          </div>
          <div className="display font-black text-xl mt-0.5">Sfida 1v1 all-in</div>
          <p className="text-slate-500 text-sm mt-1 max-w-lg">
            Chi fa più punti alla giornata prende <b>tutti i Gufi</b> del wallet dell'avversario.
            Requisiti: <b>≥ {status?.tutto_min_gufi ?? 5}</b> <GufoIcon size={12} className="inline"/>,
            max 1 sfida per giornata, e devi aver <b>vinto</b> la partita precedente.
          </p>
        </div>
        <Button
          disabled={!status?.tutto_can_challenge || busy}
          onClick={() => setOpenCreate(true)}
          className="rounded-full"
          style={{ background: status?.tutto_can_challenge ? "#ef4444" : undefined, color: status?.tutto_can_challenge ? "#fff" : undefined }}
          data-testid="tutto-create-btn"
        >
          <Swords className="w-4 h-4 mr-1.5" /> Lancia sfida
        </Button>
      </div>
      {!status?.tutto_can_challenge && status?.tutto_reason && (
        <div className="mt-3 text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          ⚠️ {status.tutto_reason}
        </div>
      )}

      {active && (
        <div className="mt-4 rounded-xl border p-3" style={{ borderColor: "#0ea5e955", background: "#0ea5e910" }} data-testid="tutto-active">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "#0ea5e9" }}>Sfida attiva · Giornata {active.matchday}</div>
          <div className="display font-bold text-sm mt-0.5">
            {active.from_user_id === user?.id ? "Tu" : active.from_team || active.from_name}
            <span className="mx-2 text-slate-400">VS</span>
            {active.target_user_id === user?.id ? "Tu" : active.target_team || active.target_name}
          </div>
          <div className="text-slate-500 text-xs mt-1">
            {active.status === "pending" ? "In attesa di risposta" : "Sfida in corso: si decide al termine della giornata."}
          </div>
        </div>
      )}

      {incomingPending.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Sfide ricevute</div>
          {incomingPending.map((c) => (
            <div key={c.id} className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid={`tutto-incoming-${c.id}`}>
              <div className="display font-bold text-sm">{c.from_team || c.from_name} ti sfida a Tutto o Niente</div>
              <div className="text-slate-600 text-xs mt-1">Giornata {c.matchday}. Se accetti, chi fa più punti prende tutto il wallet dell'altro.</div>
              {c.message && <div className="italic text-slate-500 text-xs mt-1">«{c.message}»</div>}
              <div className="flex gap-2 mt-3">
                <Button size="sm" disabled={busy} onClick={() => respond(c.id, true)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`tutto-accept-${c.id}`}>
                  <Check className="w-4 h-4 mr-1"/> Accetta
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => respond(c.id, false)} className="rounded-full" data-testid={`tutto-decline-${c.id}`}>
                  <XIcon className="w-4 h-4 mr-1"/> Rifiuta
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Storico sfide</div>
          <div className="space-y-1.5">
            {recent.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.pending;
              const iAmFrom = c.from_user_id === user?.id;
              const other = iAmFrom ? (c.target_team || c.target_name) : (c.from_team || c.from_name);
              const won = c.status === "resolved" && c.winner_id === user?.id;
              const lost = c.status === "resolved" && c.loser_id === user?.id;
              return (
                <div key={c.id} className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs" data-testid={`tutto-hist-${c.id}`}>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
                  <span className="text-slate-500">G{c.matchday}</span>
                  <span className="font-semibold text-slate-800 truncate">
                    {iAmFrom ? "Tu → " : ""}{other}{!iAmFrom ? " → Tu" : ""}
                  </span>
                  <span className="ml-auto tabular-nums font-black" style={{ color: won ? "#10b981" : lost ? "#ef4444" : "#94a3b8" }}>
                    {c.status === "resolved" ? (won ? `+${c.transfer}` : `-${c.transfer}`) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create challenge dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="bg-white border-slate-200 text-slate-900" data-testid="tutto-dialog">
          <DialogHeader>
            <DialogTitle className="display font-black text-2xl flex items-center gap-2">
              <Swords className="w-6 h-6" style={{ color: "#ef4444" }} /> Lancia una sfida Tutto o Niente
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Se accetta, alla fine della giornata chi fa più punti prende tutti i Gufi del wallet avversario.
              Il tuo wallet: <b>{status?.gufi_wallet ?? 0}</b> Gufi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="tutto-target">
                <SelectValue placeholder="Scegli l'avversario" />
              </SelectTrigger>
              <SelectContent>
                {availableTargets.length === 0 && (
                  <div className="p-2 text-slate-500 text-sm">Nessuna squadra disponibile</div>
                )}
                {availableTargets.map((t) => (
                  <SelectItem key={t.user_id} value={t.user_id}>{t.team_name} · Girone {t.girone}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea placeholder="Messaggio di sfida (opzionale)" value={message}
                      onChange={(e) => setMessage(e.target.value)} maxLength={160}
                      className="bg-slate-50 border-slate-200" data-testid="tutto-message" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpenCreate(false)} className="border-slate-300 text-slate-700 rounded-full">Annulla</Button>
              <Button onClick={createChallenge} disabled={busy} className="rounded-full text-white" style={{ background: "#ef4444" }} data-testid="tutto-submit">
                {busy ? "Invio..." : "Lancia sfida"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
